import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { cloneDeep } from 'lodash';
import { PromotedEventSportState } from 'src/main/cms/PromotedEvents/promotedEventsState/PromotedEventSportState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllPromotedSports';

export type PromotedSportDataModelType = Response200Type[number];

export type CompetitionCombinedType = PromotedSportDataModelType['competitions_list'][number];

export type EventCombinedType = CompetitionCombinedType['events_list'][number];

export class PromotedEventsState {
    private readonly promotedEventSportStateMap: MobxMapAutoNew<string, PromotedEventSportState>;

    @observable public readonly promotedEventsResource: Resource<Array<PromotedSportDataModelType>>;
    @observable.ref public sportsArray: Array<PromotedSportDataModelType> = [];
    @observable public isLoading: boolean = false;

    constructor(private readonly trpcClient: TrpcClient) {
        this.promotedEventsResource = new Resource(async (): Promise<Array<PromotedSportDataModelType>> => {
            const response = await this.trpcClient.client.promotedSports.getAllPromotedSports.query();

            if (response.status === 200) {
                return response.body;
            }

            return [];
        });

        this.promotedEventSportStateMap = new MobxMapAutoNew(
            (sportSlug: string): PromotedEventSportState =>
                new PromotedEventSportState(trpcClient, sportSlug, this.getSportData, this)
        );
        makeAutoObservable(this);
    }

    getPromotedEventSportStateBySportSlug = (sportSlug: string): PromotedEventSportState => {
        return this.promotedEventSportStateMap.get(sportSlug);
    };

    @computed public get isPromotedEventsDataLoading(): boolean {
        const promotedSportsData = this.promotedEventsResource.get();
        return promotedSportsData.type === 'loading';
    }

    @action public getSportData = (sportSlug: string): PromotedSportDataModelType | null => {
        return this.sportsArray.find((sport) => sport.sport_id === sportSlug) ?? null;
    };

    @action public setSportsDataFromResource = (): void => {
        this.promotedEventsResource.get();
        this.sportsArray = [];
        if (this.promotedEventsData === null) {
            return;
        }
        this.sportsArray = this.promotedEventsData;
    };

    @action public setSportsData = (data: Array<PromotedSportDataModelType>): void => {
        const tempArray = cloneDeep(data);
        this.sportsArray = tempArray;
    };

    @action public setIsLoading = (val: boolean): void => {
        this.isLoading = val;
    };

    @computed public get promotedEventsData(): Array<PromotedSportDataModelType> | null {
        const promotedEventsData = this.promotedEventsResource.get();
        if (promotedEventsData.type === 'ready') {
            if (promotedEventsData.value !== null) {
                return promotedEventsData.value;
            }
        }
        return null;
    }

    public getCompetitionsArrayForDraggableList = (sportId: string) => {
        return this.sportsArray.find((sportsElem) => sportsElem.sport_id === sportId)?.competitions_list ?? [];
    };

    public setCompetitionsArrayForDraggableList = (
        sportId: string,
        competitionsList: Array<CompetitionCombinedType>
    ) => {
        const tempArr = [];
        for (const value of this.sportsArray) {
            if (value.sport_id === sportId) {
                tempArr.push({
                    competitions_list: competitionsList,
                    display_order: value.display_order,
                    universe: value.universe,
                    sport_id: value.sport_id,
                    id: value.id,
                    is_active: value.is_active,
                });
            } else {
                tempArr.push(value);
            }
        }
        return tempArr;
    };

    public setEventsArrayForDraggableList = (
        sportId: string,
        competitionId: number,
        eventsList: Array<EventCombinedType>
    ) => {
        const tempArr = [];
        for (const value of this.sportsArray) {
            if (value.sport_id === sportId) {
                tempArr.push({
                    competitions_list: value.competitions_list.map((e) => {
                        if (e.competition_id === competitionId) {
                            return {
                                ...e,
                                events_list: eventsList,
                            };
                        } else {
                            return {
                                ...e,
                            };
                        }
                    }),
                    display_order: value.display_order,
                    universe: value.universe,
                    sport_id: value.sport_id,
                    id: value.id,
                    is_active: value.is_active,
                });
            } else {
                tempArr.push(value);
            }
        }
        return tempArr;
    };

    public getEventsArrayForDraggableList = (sportId: string, competitionId: number) => {
        const competitionsData = this.getCompetitionsArrayForDraggableList(sportId);
        return competitionsData.find((competition) => competition.competition_id === competitionId)?.events_list;
    };
}
